import styled from "styled-components";

export const PlayerDock = styled.aside<{
  $mobileCollapsed: boolean;
  $docked: boolean;
}>`
  position: fixed;
  z-index: 140;
  left: ${({ $docked }) => ($docked ? "0" : "50%")};
  bottom: ${({ $docked }) =>
    $docked ? "0" : "max(18px, env(safe-area-inset-bottom))"};
  width: ${({ $docked }) =>
    $docked ? "100%" : "min(460px, calc(100vw - 32px))"};
  transform: ${({ $docked }) => ($docked ? "none" : "translateX(-50%)")};
  color: var(--text);
  transition:
    left 0.32s ease,
    bottom 0.32s ease,
    width 0.32s ease,
    transform 0.32s ease;

  @media (max-width: 768px) {
    display: ${({ $mobileCollapsed }) => ($mobileCollapsed ? "none" : "block")};
    bottom: max(12px, env(safe-area-inset-bottom));
    left: 50%;
    width: calc(100vw - 24px);
    transform: translateX(-50%);
  }

  @media print {
    display: none;
  }
`;

export const MobileLauncher = styled.button<{ $hidden: boolean }>`
  display: none;

  @media (max-width: 768px) {
    position: fixed;
    z-index: 140;
    right: 16px;
    bottom: max(14px, env(safe-area-inset-bottom));
    display: ${({ $hidden }) => ($hidden ? "none" : "grid")};
    width: 50px;
    height: 50px;
    padding: 0;
    place-items: center;
    border: 1px solid rgba(255, 255, 255, 0.88);
    border-radius: 50%;
    color: #ffffff;
    background: #2f2f2d;
    box-shadow:
      0 10px 26px rgba(36, 36, 34, 0.22),
      0 2px 6px rgba(36, 36, 34, 0.14),
      inset 0 0 0 1px rgba(255, 255, 255, 0.08);
    cursor: pointer;
    transition: transform 0.18s ease, background-color 0.18s ease;

    &:hover {
      background: #171716;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0) scale(0.97);
    }

    &:focus-visible {
      outline: 2px solid #353533;
      outline-offset: 3px;
    }

    svg {
      width: 16px;
      height: 16px;
      margin-left: 2px;
    }
  }

  @media print {
    display: none;
  }
`;

export const PlayerShell = styled.div<{ $docked: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  box-sizing: border-box;
  min-height: ${({ $docked }) => ($docked ? "66px" : "82px")};
  padding: ${({ $docked }) =>
    $docked
      ? "7px max(24px, calc((100vw - 1180px) / 2)) 17px"
      : "9px 11px 22px"};
  overflow: hidden;
  border: 1px solid rgba(68, 68, 68, 0.24);
  border-radius: ${({ $docked }) => ($docked ? "0" : "10px")};
  background: var(--surface);
  box-shadow: ${({ $docked }) =>
    $docked
      ? "0 -8px 24px rgba(36, 36, 34, 0.1)"
      : "0 12px 34px rgba(36, 36, 34, 0.12), 0 2px 7px rgba(36, 36, 34, 0.08)"};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  transition:
    min-height 0.32s ease,
    padding 0.32s ease,
    border-radius 0.32s ease,
    box-shadow 0.32s ease;

  @media (max-width: 768px) {
    grid-template-columns: 44px minmax(0, 1fr) auto;
    gap: 10px;
    min-height: 76px;
    padding: 8px 9px 20px;
    border-radius: 9px;
  }
`;

export const CollapseButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    position: absolute;
    z-index: 3;
    top: -11px;
    right: -4px;
    display: grid;
    width: 27px;
    height: 27px;
    padding: 0;
    place-items: center;
    border: 1px solid rgba(68, 68, 68, 0.24);
    border-radius: 50%;
    color: var(--subtle);
    background: var(--surface);
    box-shadow: 0 3px 8px rgba(36, 36, 34, 0.1);
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid #353533;
      outline-offset: 2px;
    }

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

export const Artwork = styled.img<{ $docked: boolean }>`
  display: block;
  width: ${({ $docked }) => ($docked ? "42px" : "48px")};
  height: ${({ $docked }) => ($docked ? "42px" : "48px")};
  border-radius: 5px;
  object-fit: cover;
  background: var(--surface);
  transition: width 0.32s ease, height 0.32s ease;

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
  }
`;

export const PlayerDetails = styled.div`
  min-width: 0;
`;

export const PlaylistName = styled.div`
  margin-bottom: 2px;
  overflow: hidden;
  color: var(--muted);
  font-family: "Aktiv Grotesk Corp", "DM Sans", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  letter-spacing: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TrackTitle = styled.div`
  overflow: hidden;
  color: var(--heading);
  font-size: 15px;
  font-weight: 500;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 18px;
  }
`;

export const ArtistName = styled.div`
  overflow: hidden;
  margin-top: 1px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ProgressRow = styled.div<{ $docked: boolean }>`
  position: absolute;
  right: ${({ $docked }) =>
    $docked ? "max(24px, calc((100vw - 1180px) / 2))" : "11px"};
  bottom: ${({ $docked }) => ($docked ? "5px" : "7px")};
  left: ${({ $docked }) =>
    $docked ? "max(24px, calc((100vw - 1180px) / 2))" : "11px"};
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 28px;
  gap: 8px;
  align-items: center;

  @media (max-width: 768px) {
    right: 9px;
    bottom: 6px;
    left: 9px;
  }
`;

export const ProgressTrack = styled.div`
  height: 2px;
  overflow: hidden;
  border-radius: 2px;
  background: var(--line);
`;

export const ProgressValue = styled.div<{ percentage: number }>`
  width: ${({ percentage }) => percentage}%;
  height: 100%;
  border-radius: inherit;
  background: var(--text);
  transition: width 0.2s linear;
`;

export const Time = styled.span`
  color: var(--muted);
  font-size: 9px;
  line-height: 10px;
  font-variant-numeric: tabular-nums;

  &:last-child {
    text-align: right;
  }
`;

export const PlayButton = styled.button`
  display: grid;
  width: 32px;
  height: 32px;
  padding: 0;
  place-items: center;
  border: 1px solid rgba(68, 68, 68, 0.46);
  border-radius: 50%;
  color: var(--text);
  background: transparent;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--text);
    color: var(--page);
    background: var(--text);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid #353533;
    outline-offset: 3px;
  }

  &:disabled {
    color: #a4a19a;
    border-color: #d1cfc9;
    cursor: wait;
  }

  svg {
    display: block;
    width: 15px;
    height: 15px;
  }

  @media (max-width: 768px) {
    width: 31px;
    height: 31px;
  }
`;

export const PlayerControls = styled.div`
  display: flex;
  gap: 3px;
  align-items: center;
`;

export const SkipButton = styled.button`
  display: grid;
  width: 28px;
  height: 30px;
  padding: 0;
  place-items: center;
  border: 0;
  border-radius: 6px;
  color: var(--subtle);
  background: transparent;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover:not(:disabled) {
    color: var(--heading);
    background: rgba(68, 68, 68, 0.075);
  }

  &:focus-visible {
    outline: 2px solid #353533;
    outline-offset: 1px;
  }

  &:disabled {
    color: #b7b4ad;
    cursor: wait;
  }

  svg {
    display: block;
    width: 15px;
    height: 15px;
  }

  @media (max-width: 380px) {
    width: 27px;
  }
`;

export const HiddenEmbed = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;

`;

export const PlayerClearance = styled.div<{
  $mobileCollapsed: boolean;
  $docked: boolean;
}>`
  height: ${({ $docked }) => ($docked ? 68 : 72)}px;
  pointer-events: none;

  @media (max-width: 768px) {
    height: ${({ $mobileCollapsed }) => ($mobileCollapsed ? 58 : 76)}px;
  }

  @media print {
    display: none;
  }
`;
